import SearchBoxLayout from "@/components/layout/SearchBoxLayout";
import { Button } from "@/components/ui/Button";
import PrintButton from "@/components/ui/PrintButton";
import ExcelButton from "@/components/ui/ExcelButton";
import { InputBox } from "@/components/ui/InputBox";
import CountBox from "@/components/ui/CountBox";
import { useMemo, useState, useEffect, useCallback } from "react";
import { Footer } from "@/components/layout/Footer";
import Table, { TableColumn } from "@/components/ui/Table";
import { formatNumber, sumBy } from "@/lib/utils";
import DateBox from "@/components/ui/DateBox";
import { useFreight } from "@/hooks/management/freight/useFreight";
import DateSearchBtn from "@/components/ui/DateSearchBtn";
import SelectBox from "@/components/ui/SelectBox";
import { getToday } from "@/lib/date";
import { CheckBox } from "@/components/ui/CheckBox";

export default function SalesByClientPage() {
  const [searchValue, setSearchValue] = useState("");
  const [actualSearchValue, setActualSearchValue] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [rangeBtn, setRangeBtn] = useState("today");
  const [tableVer, setTableVer] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [allData, setAllData] = useState<any[]>([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searchType, setSearchType] = useState("itemName");
  const [searchActualType, setSearchActualType] = useState("itemName");
  const [exactMatch, setExactMatch] = useState(false);
  const [actualExactMatch, setActualExactMatch] = useState(false);

  const {
    data: freightData,
    isLoading,
    error,
  } = useFreight({
    searchType: searchActualType,
    search: actualSearchValue,
    dateFrom: startDate,
    dateTo: endDate,
    exactMatch: actualExactMatch,
    page: currentPage,
    size: 20,
  });

  useEffect(() => {
    const today = getToday();
    setStartDate(today);
    setEndDate(today);
  }, []);

  const handleReset = () => {
    setSearchType("itemName");
    setSearchActualType("itemName");
    setSearchValue("");
    setActualSearchValue("");
    setExactMatch(false);
    setActualExactMatch(false);
    const today = getToday();
    setStartDate(today);
    setEndDate(today);
    setCurrentPage(0);
    setTotalElements(0);
    setRangeBtn("today");
    setLoadingMore(false);
    setAllData([]);
    setTableVer(v => v + 1);
  };

  const handleSearch = useCallback(() => {
    setActualSearchValue(searchValue);
    setSearchActualType(searchType);
    setActualExactMatch(exactMatch);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
    setTableVer(v => v + 1);
  }, [searchValue, searchType, exactMatch]);

  // 테이블 컬럼
  const columns: TableColumn[] = [
    { key: "saleSlipDate", header: "매출일자", bodyAlign: "left" },
    { key: "stCompanyNameShort", header: "화주명", bodyAlign: "center" },
    { key: "stItemName", header: "화물종류", bodyAlign: "center"},
    { key: "saleSlipInQty", header: "무게", bodyAlign: "center" },
    { key: "saleSlipOutArea1", header: "출발지점", bodyAlign: "center", group : "운송구간"},
    { key: "saleSlipInLocation", header: "도착지점", bodyAlign: "center", group : "운송구간" },
    { key: "stCompanyNameShort2", header: "운송사명", bodyAlign: "left" },
    { key: "stCompanyTel", header: "전화번호", bodyAlign: "left" },
    { key: "stVehicleNumber2", header: "차량번호", bodyAlign: "left" },
    { key: "stGBname", header: "차종", bodyAlign: "center" },
    { key: "stDriverName", header: "운전자", bodyAlign: "left" },
    { key: "saleSlipSaleSupportAmt", header: "계약운임", bodyAlign: "left", group : "윤임 및 요금" },
    { key: "saleSlipBuySupportAmt", header: "운송운임", bodyAlign: "left", group : "윤임 및 요금" },
    { key: "saleSlipProfitAmt", header: "주선요금", bodyAlign: "left", group : "윤임 및 요금" },
    { key: "saleSlipRemark", header: "비고", bodyAlign: "left" },
  ];

  // Footer
  const footerGrid = useMemo(() => {
    const totalQty = sumBy(allData, 'saleSlipInQty');
    const totalSales = sumBy(allData, 'saleSlipSaleSupportAmt');
    const totalPurchase = sumBy(allData, 'saleSlipBuySupportAmt');
    const totalMargin = sumBy(allData, 'saleSlipProfitAmt');

    return [[
      { label: "운반량", value: formatNumber(totalQty) },
      { label: "매출액", value: formatNumber(totalSales) },
      { label: "매입액", value: formatNumber(totalPurchase) },
      { label: "운송마진", value: formatNumber(totalMargin) },
    ]];
  }, [allData]);

  // 날짜 변경 시 실시간 조회
  useEffect(() => {
    setAllData([]);
    setCurrentPage(0);
    setTotalElements(0);
    setLoadingMore(false);
    setTableVer(v => v + 1);
  }, [actualSearchValue, startDate, endDate, actualExactMatch]);

  // 새로운 페이지 데이터가 로드되면 누적 데이터에 추가
  useEffect(() => {
    if (freightData && freightData.content && freightData.content.length > 0) {
      // 첫 번째 페이지(currentPage === 0)이거나 검색이 변경된 경우 기존 데이터 교체
      if (currentPage === 0) {
        setAllData(freightData.content);
        setTotalElements(freightData.totalElements || 0);
      } else {
        // 추가 페이지는 기존 데이터에 추가
        setAllData(prev => [...prev, ...freightData.content]);
      }
    } else if (freightData && (!freightData.content || freightData.content.length === 0)) {
      // 데이터가 없는 경우 빈 배열로 설정
      setAllData([]);
      setTotalElements(0);
    }
  }, [freightData, currentPage]);

  useEffect(() => {
    if (freightData) {
      setLoadingMore(false);
    }
  }, [freightData]);

  const hasMore = useMemo(() => {
    if (!freightData) return false;
    return !freightData.last && allData.length < totalElements;
  }, [freightData?.last, allData.length, totalElements]);

    // Table의 onLoadMore 콜백
  const handleTableLoadMore = useCallback(
    async (currentCount: number) => {
      if (!hasMore || loadingMore) {
        return;
      }

      // 현재 표시된 데이터가 누적된 데이터의 80% 이상일 때 다음 페이지 요청
      if (currentCount >= allData.length * 0.8) {
        setLoadingMore(true);
        setCurrentPage(prev => prev + 1);
      }
    },
    [hasMore, loadingMore, allData.length]
  );

  const handleDateSelect = (range: { startDate: string; endDate: string }) => {
    setStartDate(range.startDate);
    setEndDate(range.endDate);
  };

  return (
    <div className="flex flex-col h-full">
      {/* 검색영역 */}
      <SearchBoxLayout>
        <div className="flex items-end">
          <SelectBox
            label="검색어"
            className="w-25"
            value={searchType}
            onChange={(v) => setSearchType(v as "stItemName" | "stItemCode")}
            options={[
              { label: "화물종류", value: "itemName" },
              { label: "출발지점", value: "saleSlipOutArea1" },
              { label: "도착지점", value: "saleSlipOutArea2" },
              { label: "차량번호", value: "stVehicleNumber2" },
              { label: "운송사명", value: "stCompanyNameShort" }
            ]}
          />
          <div className="mr-1" />
          <InputBox
            label="검색어"
            className="w-50"
            value={searchValue}
            onChange={e => setSearchValue(e.target.value)}
            onKeyDown={e => e.key === "Enter" && handleSearch()}
          />
          <div className="mr-3" />
          <DateBox
            label="매출일자"
            startDate={startDate}
            endDate={endDate}
            onStartDateChange={setStartDate}
            onEndDateChange={setEndDate}
          />
          <div className="mr-3" />
          <DateSearchBtn
            mode="range"
            value={rangeBtn}
            onChangeValue={setRangeBtn}
            currentRange={{ startDate, endDate }}
            onDateSelect={handleDateSelect}
          />
          <div className="flex gap-[6px] ml-auto items-end">
            <CheckBox
              label="정확히 일치"
              checked={exactMatch}
              onChange={(e) => setExactMatch(e.target.checked)}
              labelPosition="right"
            />
            <Button variant="reset" onClick={handleReset}>초기화</Button>
            <Button variant="search" onClick={handleSearch}>조회</Button>
          </div>
        </div>
      </SearchBoxLayout>

      {/* 상단 버튼/카운트 */}
      <main className="flex-1 px-4 flex flex-col min-h-0">
        <div className="flex items-center justify-between mb-2">
          <CountBox count={allData.length} />
          <div className="flex gap-2">
            <PrintButton data={allData} columns={columns} title="화물운송실적보고관리대장" skipPermissionCheck />
            <ExcelButton data={allData} columns={columns} fileName="화물운송실적보고관리대장" skipPermissionCheck />
          </div>
        </div>

        {/* 본문 표 */}
        <div className="flex-1 min-h-0">
          <Table
            key={`freight-table-v${tableVer}`}
            data={allData}
            className="h-full"
            infiniteScroll={true}
            initialLoadCount={20}
            loadMoreCount={20}
            hasMore={hasMore}
            onLoadMore={handleTableLoadMore}
            loadingMore={loadingMore}
            columns={columns}
            loading={isLoading && allData.length === 0}
            showCheckbox={true}
            showRowNumber={true}
            maxHeight="100%" />
        </div>
        {isLoading && allData.length > 0 && <div className="text-center py-2 text-gray-500">새 페이지 로딩 중...</div>}
      </main>

      {/* 푸터 총계 */}
      <footer className="flex-shrink-0 p-4 bg-gray-50 border-t">
        <Footer grid={footerGrid} />
      </footer>
    </div>
  );
}
